/**
 * ═══════════════════════════════════════════════════════════════════════════
 * READING a spreadsheet — the other half of the writer, for ADM-03's import.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ── Why this lives beside the writer ───────────────────────────────────────
 *
 * ADM-03 §6 downloads the TEMPLATE as .xlsx, written by `buildXlsx` in this
 * package, and §5 field 2 then accepts "CSV or XLSX" back. A reader that did
 * not sit next to the writer would be a second opinion about what a cell is,
 * and the one file the import is guaranteed to be handed is the one this
 * package produced. `readXlsx(buildXlsx(x))` is a round trip the suite asserts.
 *
 * ── What it must cope with that the writer never emits ─────────────────────
 *
 * The other file the import is guaranteed to be handed is one Excel saved, and
 * Excel writes four things `buildXlsx` does not:
 *
 *   DEFLATED entries. The writer stores everything (method 0) so it needs no
 *     compression primitive in the browser; Excel deflates. `DecompressionStream
 *     ('deflate-raw')` is the one primitive both runtimes have, which is why
 *     this function is async and `buildXlsx` is not.
 *   A SHARED STRING TABLE. Text cells become `t="s"` with an index into
 *     xl/sharedStrings.xml rather than the inline `<is>` the writer emits.
 *   SPARSE ROWS. An empty cell is simply absent, so a row's cells must be
 *     placed by their `r="C7"` reference and the gaps filled — reading them
 *     positionally shifts every column after the first blank, which is the bug
 *     that makes an import quietly put a phone number in the email column.
 *   DATE CELLS. Excel has no date type: a date is a number wearing a number
 *     FORMAT, so telling a date from a quantity means reading styles.xml. A
 *     serial that reached a validator as "45870" would be refused with a
 *     sentence about the wrong thing.
 *
 * ── What it deliberately does not do ───────────────────────────────────────
 *
 * Formulas are read as their CACHED VALUE (`<v>` under `t="str"` or a bare
 * numeric), never evaluated. A spreadsheet full of `=VLOOKUP(...)` imports the
 * figures the person saw, which is the only honest reading of a file somebody
 * checked before uploading. ZIP64, encryption and multi-sheet selection are
 * absent for the same reasons the writer omits them; the first sheet is the
 * one the template has and the one the import reads.
 */
// ── CSV ─────────────────────────────────────────────────────────────────────
/**
 * RFC 4180 with the two concessions every real file needs: a leading UTF-8 BOM
 * (which `downloadCsv` itself writes, so Excel keeps Arabic) and CRLF.
 *
 * Written as a character loop rather than a split-on-comma because a quoted
 * field may contain commas, newlines and doubled quotes — and an address
 * column contains all three. A regex that gets this right is unreadable; a
 * loop that gets it right is fifteen lines.
 */
export function parseCsv(source) {
    const input = source.charCodeAt(0) === 0xfeff ? source.slice(1) : source;
    const rows = [];
    let row = [];
    let field = '';
    let quoted = false;
    let touched = false;
    const endField = () => {
        row.push(field);
        field = '';
        touched = true;
    };
    const endRow = () => {
        endField();
        rows.push(row);
        row = [];
        touched = false;
    };
    for (let i = 0; i < input.length; i += 1) {
        const ch = input[i];
        if (quoted) {
            if (ch !== '"') {
                field += ch;
            }
            else if (input[i + 1] === '"') {
                field += '"';
                i += 1;
            }
            else {
                quoted = false;
            }
            continue;
        }
        if (ch === '"' && field === '') {
            quoted = true;
            touched = true;
        }
        else if (ch === ',') {
            endField();
        }
        else if (ch === '\n') {
            endRow();
        }
        else if (ch !== '\r') {
            field += ch;
            touched = true;
        }
    }
    // A file ending in a newline must not produce a phantom final row — that row
    // would be reported as "row 501 is missing every required column" on a
    // 500-row file, which is a confusing way to say nothing is wrong.
    if (touched || field !== '')
        endRow();
    return rows;
}
// ── XLSX ────────────────────────────────────────────────────────────────────
export async function readXlsx(bytes) {
    const parts = await unzip(bytes);
    const sheet = parts.get(sheetPath(parts));
    if (sheet === undefined) {
        throw new SpreadsheetError('That file is not a spreadsheet this product can read.');
    }
    const shared = sharedStrings(parts.get('xl/sharedStrings.xml'));
    const dateStyles = dateStyleIndexes(parts.get('xl/styles.xml'));
    return sheetGrid(sheet, shared, dateStyles);
}
/** A file that is not a readable spreadsheet at all — ADM-03 §14's first row. */
export class SpreadsheetError extends Error {
}
/**
 * Which part holds the FIRST sheet.
 *
 * Resolved through workbook.xml → workbook.xml.rels rather than assuming
 * `sheet1.xml`, because a workbook whose first tab was deleted and re-added
 * keeps the tab order in workbook.xml and the file names out of step with it.
 * Falls back to the lowest-named worksheet part, which is right for every file
 * that has only one.
 */
function sheetPath(parts) {
    const workbook = parts.get('xl/workbook.xml');
    const rels = parts.get('xl/_rels/workbook.xml.rels');
    if (workbook !== undefined && rels !== undefined) {
        const first = /<sheet\b[^>]*?r:id="([^"]+)"/.exec(workbook);
        if (first) {
            const target = new RegExp(`<Relationship\\b[^>]*?Id="${first[1].replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"[^>]*?Target="([^"]+)"`).exec(rels);
            if (target) {
                const path = target[1].replace(/^\/?(xl\/)?/, '');
                if (parts.has(`xl/${path}`))
                    return `xl/${path}`;
            }
        }
    }
    const worksheets = [...parts.keys()].filter((name) => name.startsWith('xl/worksheets/')).sort();
    return worksheets[0] ?? 'xl/worksheets/sheet1.xml';
}
/** `<si>` order IS the index space; `t="s"` cells hold an offset into it. */
function sharedStrings(xml) {
    if (xml === undefined)
        return [];
    return [...xml.matchAll(/<si\b[^>]*>([\s\S]*?)<\/si>/g)].map((match) => runText(match[1]));
}
/**
 * The text of an `<si>` or an `<is>`.
 *
 * Concatenated across `<r>` runs, because Excel splits a string the moment one
 * word in it is bold — and a partner name typed with an accidental bold letter
 * would otherwise import as only the un-bolded half.
 */
function runText(fragment) {
    const pieces = [...fragment.matchAll(/<t\b[^>]*>([\s\S]*?)<\/t>/g)].map((match) => unescapeXml(match[1]));
    return pieces.join('');
}
function unescapeXml(value) {
    return value
        .replace(/&#x([0-9a-fA-F]+);/g, (_, hex) => String.fromCodePoint(parseInt(hex, 16)))
        .replace(/&#(\d+);/g, (_, dec) => String.fromCodePoint(parseInt(dec, 10)))
        .replaceAll('&quot;', '"')
        .replaceAll('&apos;', "'")
        .replaceAll('&lt;', '<')
        .replaceAll('&gt;', '>')
        // Last, or `&amp;lt;` decodes twice.
        .replaceAll('&amp;', '&');
}
/**
 * The style indexes that mean "this number is a date".
 *
 * Excel's built-in date formats are 14-22 and 45-47; anything else is a date
 * only if its custom `formatCode` mentions a day, month or year outside a
 * literal. `s="N"` on a cell indexes `cellXfs`, so the answer is a set of
 * POSITIONS in that list, not of numFmtIds.
 */
function dateStyleIndexes(xml) {
    const dates = new Set();
    if (xml === undefined)
        return dates;
    const builtin = new Set([14, 15, 16, 17, 18, 19, 20, 21, 22, 45, 46, 47]);
    const custom = new Set();
    for (const match of xml.matchAll(/<numFmt\b[^>]*?numFmtId="(\d+)"[^>]*?formatCode="([^"]*)"/g)) {
        // Strip quoted literals and escaped characters first: `"May"` and `\d` are
        // text, not a month and a day.
        const code = unescapeXml(match[2])
            .replace(/"[^"]*"/g, '')
            .replace(/\\./g, '');
        if (/[ymd]/i.test(code))
            custom.add(parseInt(match[1], 10));
    }
    const cellXfs = /<cellXfs\b[^>]*>([\s\S]*?)<\/cellXfs>/.exec(xml);
    if (cellXfs === null)
        return dates;
    let index = 0;
    for (const xf of cellXfs[1].matchAll(/<xf\b[^>]*>/g)) {
        const id = /numFmtId="(\d+)"/.exec(xf[0]);
        const numFmtId = id === null ? 0 : parseInt(id[1], 10);
        if (builtin.has(numFmtId) || custom.has(numFmtId))
            dates.add(index);
        index += 1;
    }
    return dates;
}
function sheetGrid(xml, shared, dateStyles) {
    const grid = [];
    let width = 0;
    // The attribute run is LAZY, and that is not a style choice. Greedy, `[^>]*`
    // first swallows the closing `/` of a self-closing `<c r="A2"/>`, the `/>`
    // branch then fails, and the `>` branch succeeds against the NEXT cell's
    // `</c>` — so two empty cells and the value after them read as one cell, and
    // the value disappears. Lazy tries the short attribute run first and lands on
    // the self-closing branch.
    for (const rowMatch of xml.matchAll(/<row\b([^>]*?)(?:\/>|>([\s\S]*?)<\/row>)/g)) {
        const rowNumber = attr(rowMatch[1], 'r');
        const cells = [];
        for (const cellMatch of (rowMatch[2] ?? '').matchAll(/<c\b([^>]*?)(?:\/>|>([\s\S]*?)<\/c>)/g)) {
            const attributes = cellMatch[1];
            const body = cellMatch[2] ?? '';
            // SPARSE: place by reference, never by arrival order.
            const ref = attr(attributes, 'r');
            const column = ref === null ? cells.length : columnIndex(ref);
            while (cells.length < column)
                cells.push('');
            cells[column] = cellText(attributes, body, shared, dateStyles);
        }
        // Rows are sparse too — `<row r="9">` after `<row r="4">` means four blank
        // rows, and a blank row in the middle of a file is a row the import must
        // still be able to name by its spreadsheet number.
        const at = rowNumber === null ? grid.length : parseInt(rowNumber, 10) - 1;
        while (grid.length < at)
            grid.push([]);
        grid[at] = cells;
        width = Math.max(width, cells.length);
    }
    // Ragged rows are squared off so a caller can index a column without
    // checking length on every cell — the header row fixes the width the import
    // then validates against.
    for (const row of grid) {
        while (row.length < width)
            row.push('');
    }
    return grid;
}
function attr(attributes, name) {
    const match = new RegExp(`\\b${name}="([^"]*)"`).exec(attributes);
    return match === null ? match : match[1];
}
/** "BC12" → 54. Zero-based, so it indexes the row array directly. */
export function columnIndex(ref) {
    const letters = /^([A-Z]+)/.exec(ref.toUpperCase());
    if (letters === null)
        return 0;
    let n = 0;
    for (const ch of letters[1])
        n = n * 26 + (ch.charCodeAt(0) - 64);
    return n - 1;
}
function cellText(attributes, body, shared, dateStyles) {
    const type = attr(attributes, 't') ?? 'n';
    if (type === 'inlineStr')
        return runText(body);
    if (type === 'e')
        return ''; // #REF!, #VALUE! — a hole, not a value.
    const raw = /<v\b[^>]*>([\s\S]*?)<\/v>/.exec(body);
    const value = raw === null ? '' : unescapeXml(raw[1]);
    if (value === '')
        return '';
    if (type === 's')
        return shared[parseInt(value, 10)] ?? '';
    if (type === 'str')
        return value;
    if (type === 'b')
        return value === '1' ? 'true' : 'false';
    // A number. Only now does the style matter.
    const style = attr(attributes, 's');
    if (style !== null && dateStyles.has(parseInt(style, 10))) {
        return isoFromSerial(value);
    }
    // The DIGITS AS WRITTEN. Never `Number(value)` — §2.1 keeps money out of a
    // JS number, and this is the exact point at which a spreadsheet's amount
    // would become one.
    return value;
}
/**
 * The inverse of `dateSerial` — days since 1899-12-30, back to `yyyy-mm-dd`.
 *
 * A fractional serial is a date-and-time; the date part is what a master-data
 * sheet means, so the fraction is dropped rather than rounded (23:30 must not
 * become tomorrow).
 */
export function isoFromSerial(serial) {
    const days = Math.floor(Number(serial));
    if (!Number.isFinite(days) || days <= 0)
        return serial;
    const ms = Date.UTC(1899, 11, 30) + days * 86_400_000;
    return new Date(ms).toISOString().slice(0, 10);
}
// ── The zip reader ──────────────────────────────────────────────────────────
/**
 * Entries by name, each decoded as UTF-8 text — every part of an .xlsx is XML.
 *
 * Read from the CENTRAL DIRECTORY rather than by walking local headers,
 * because a local header may carry zeroed sizes with the real ones in a data
 * descriptor after the payload (general-purpose bit 3), which several writers
 * emit and which cannot be parsed forwards at all.
 */
async function unzip(bytes) {
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    const end = findEndOfCentralDirectory(view);
    if (end === null)
        throw new SpreadsheetError(NOT_A_SPREADSHEET);
    const count = view.getUint16(end + 10, true);
    let at = view.getUint32(end + 16, true);
    const decoder = new TextDecoder();
    const parts = new Map();
    for (let i = 0; i < count; i += 1) {
        if (at + 46 > bytes.length || view.getUint32(at, true) !== 0x02014b50) {
            throw new SpreadsheetError(NOT_A_SPREADSHEET);
        }
        const method = view.getUint16(at + 10, true);
        const compressedSize = view.getUint32(at + 20, true);
        const nameLength = view.getUint16(at + 28, true);
        const extraLength = view.getUint16(at + 30, true);
        const commentLength = view.getUint16(at + 32, true);
        const localOffset = view.getUint32(at + 42, true);
        const name = decoder.decode(bytes.subarray(at + 46, at + 46 + nameLength));
        if (view.getUint32(localOffset, true) !== 0x04034b50) {
            throw new SpreadsheetError(NOT_A_SPREADSHEET);
        }
        const dataAt = localOffset +
            30 +
            view.getUint16(localOffset + 26, true) +
            view.getUint16(localOffset + 28, true);
        const payload = bytes.subarray(dataAt, dataAt + compressedSize);
        // Only the parts the reader actually opens are inflated. A workbook with a
        // megabyte of pivot cache and calc chain should not cost the import a
        // megabyte of inflate.
        if (WANTED.test(name)) {
            parts.set(name, decoder.decode(method === 0 ? payload : await inflateRaw(payload)));
        }
        at += 46 + nameLength + extraLength + commentLength;
    }
    return parts;
}
const NOT_A_SPREADSHEET = 'That file is not a spreadsheet this product can read.';
const WANTED = /^xl\/(workbook\.xml|_rels\/workbook\.xml\.rels|styles\.xml|sharedStrings\.xml|worksheets\/[^/]+\.xml)$/;
/**
 * The end-of-central-directory record, found by scanning BACKWARDS.
 *
 * It is the last 22 bytes of the file unless the archive carries a comment, so
 * the scan is bounded by the maximum comment length rather than by the file —
 * a 10 MB upload must not become a 10 MB backwards search on a file that is
 * not a zip at all.
 */
function findEndOfCentralDirectory(view) {
    const floor = Math.max(0, view.byteLength - 22 - 0xffff);
    for (let at = view.byteLength - 22; at >= floor; at -= 1) {
        if (view.getUint32(at, true) === 0x06054b50)
            return at;
    }
    return null;
}
/**
 * `DecompressionStream('deflate-raw')` — the one inflate both runtimes have.
 *
 * Node's `zlib` is not in a browser and `pako` is a dependency in the bundle
 * the customer's browser executes, which the writer's own header rules out for
 * this package.
 */
async function inflateRaw(payload) {
    const stream = new Blob([payload])
        .stream()
        .pipeThrough(new DecompressionStream('deflate-raw'));
    return new Uint8Array(await new Response(stream).arrayBuffer());
}
//# sourceMappingURL=read.js.map